import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import TopBarProgress from 'react-topbar-progress-indicator';

import RafPage from '@modules/raf/RafPage';
import { WithChildren } from '@shared/helpers';

import { getCSSVariableValue } from '../../shared/assets/ts/_utils';
import { MasterLayout } from '../../shared/layout/MasterLayout';
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper';

// Declared once at module scope. Created inside PrivateRoutes, every re-render
// (each navigation and auth change re-renders it) made new lazy components, so
// the current page suspended again and flashed the loading bar.
const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'));
const OrdersPage = lazy(() => import('../modules/orders/OrdersPage'));
const SubscriptionPage = lazy(() => import('../modules/subscriptions/SubscriptionPage'));
const ShopPage = lazy(() => import('../modules/shop/ShopPage'));
const StoreCreditPage = lazy(() => import('../modules/store-credit/StoreCreditPage'));

const PrivateRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />
        {/* Pages */}
        <Route path="dashboard" element={<DashboardWrapper />} />
        {/* Lazy Modules */}
        <Route
          path="/profile/*"
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        <Route
          path="/orders"
          element={
            <SuspensedView>
              <OrdersPage />
            </SuspensedView>
          }
        />

        <Route
          path="/subscriptions"
          element={
            <SuspensedView>
              <SubscriptionPage />
            </SuspensedView>
          }
        />

        <Route
          path="/raf"
          element={
            <SuspensedView>
              <RafPage />
            </SuspensedView>
          }
        />

        <Route
          path="/mitbestellen"
          element={
            <SuspensedView>
              <ShopPage />
            </SuspensedView>
          }
        />

        {/* Legacy path: the tab was renamed from "Shop" to "Mitbestellen".
            Keep redirecting so old bookmarks and campaign links still land. */}
        <Route path="/shop" element={<Navigate to="/mitbestellen" replace />} />

        <Route
          path="/store-credit"
          element={
            <SuspensedView>
              <StoreCreditPage />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--kt-primary');
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
